import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, format, startOfWeek } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Card from '../../ui/Card';
import { Modal } from '../../ui';
import LoadingSpinner from '../../common/LoadingSpinner';
import { adminAnalyticsAPI } from '../../../services/api';
import { CalendarBooking, ClinicCalendar } from '../../../types/analytics';
import { BUCKET_COLOR, BUCKET_HELP, BUCKET_LABEL, fmtHours } from './format';
import { apiErrorMessage } from '../../../utils/apiError';

const PX_PER_HOUR = 44;
const toMin = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const hourLabel = (h: number): string => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;
const timeLabel = (hhmm: string): string => {
  const m = toMin(hhmm);
  const h = Math.floor(m / 60);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * Whole-clinic calendar: pick a day from the week strip, see every room side
 * by side. Blocks use the same completed / upcoming / held colours as the
 * snapshot so the two views read as one system.
 */
const ClinicCalendarTab: React.FC = () => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selected, setSelected] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [data, setData] = useState<ClinicCalendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReleased, setShowReleased] = useState(false);
  const [detail, setDetail] = useState<CalendarBooking | null>(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      setData(await adminAnalyticsAPI.getCalendar(format(weekStart, 'yyyy-MM-dd'), format(addDays(weekStart, 6), 'yyyy-MM-dd')));
    } catch (e: any) {
      setError(apiErrorMessage(e, 'Could not load the calendar'));
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  // Keep the selected day inside the visible week.
  useEffect(() => {
    const iso = days.map(d => format(d, 'yyyy-MM-dd'));
    if (!iso.includes(selected)) setSelected(iso[0]);
  }, [days, selected]);

  const visible = useMemo(
    () => (data?.bookings || []).filter(b => (showReleased || b.bucket !== 'released')),
    [data, showReleased]
  );
  const dayBookings = visible.filter(b => b.date === selected);
  const hoursByDay = useMemo(() => {
    const m: Record<string, number> = {};
    visible.filter(b => b.bucket !== 'released' && b.bucket !== 'pending')
      .forEach(b => { m[b.date] = (m[b.date] || 0) + (b.duration_minutes + b.buffer_minutes) / 60; });
    return m;
  }, [visible]);

  // Visible hour range: 8-20 by default, stretched to fit the day's bookings.
  const startHour = Math.min(8, ...dayBookings.map(b => Math.floor(toMin(b.start) / 60)));
  const endHour = Math.max(20, ...dayBookings.map(b => Math.ceil(toMin(b.end) / 60)));
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const rooms = (data?.rooms || []).filter(r => r.is_active || dayBookings.some(b => b.room_id === r.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Previous week" onClick={() => setWeekStart(w => addDays(w, -7))}
            className="p-1.5 rounded-lg border border-clinic-primary-lighter bg-white hover:bg-clinic-cream"><ChevronLeftIcon className="h-4 w-4" /></button>
          <button type="button" onClick={() => { setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 })); setSelected(format(new Date(), 'yyyy-MM-dd')); }}
            className="px-3 py-1.5 text-sm rounded-lg border border-clinic-primary-lighter bg-white hover:bg-clinic-cream">Today</button>
          <button type="button" aria-label="Next week" onClick={() => setWeekStart(w => addDays(w, 7))}
            className="p-1.5 rounded-lg border border-clinic-primary-lighter bg-white hover:bg-clinic-cream"><ChevronRightIcon className="h-4 w-4" /></button>
          <span className="ml-2 text-sm font-medium text-clinic-dark">
            {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs text-clinic-dark flex-wrap">
          {(['completed', 'upcoming', 'held'] as const).map(k => (
            <span key={k} className="inline-flex items-center gap-1.5" title={BUCKET_HELP[k]}>
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: BUCKET_COLOR[k] }} aria-hidden="true" />{BUCKET_LABEL[k]}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5" title={BUCKET_HELP.pending}>
            <span className="inline-block w-3 h-3 rounded-sm border border-dashed border-clinic-muted" aria-hidden="true" />Pending
          </span>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showReleased} onChange={e => setShowReleased(e.target.checked)}
              className="h-3.5 w-3.5 rounded text-clinic-primary border-clinic-primary-light" />
            Show released
          </label>
        </div>
      </div>

      {/* Week strip */}
      <div className="grid grid-cols-7 gap-2" role="tablist" aria-label="Day">
        {days.map(d => {
          const iso = format(d, 'yyyy-MM-dd');
          const active = iso === selected;
          return (
            <button key={iso} type="button" role="tab" aria-selected={active} onClick={() => setSelected(iso)}
              className={`rounded-lg border px-2 py-2 text-center transition-colors ${active
                ? 'border-clinic-primary bg-white shadow-clinic-sm' : 'border-clinic-primary-lighter bg-white/60 hover:bg-white'}`}>
              <span className="block text-xs text-clinic-muted">{format(d, 'EEE')}</span>
              <span className="block text-base font-semibold text-clinic-dark">{format(d, 'd')}</span>
              <span className="block text-[11px] text-clinic-muted">{hoursByDay[iso] ? fmtHours(hoursByDay[iso]) : 'no bookings'}</span>
            </button>
          );
        })}
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><LoadingSpinner /></div>
        ) : error ? (
          <p className="p-6 text-sm text-clinic-accent">{error}</p>
        ) : rooms.length === 0 ? (
          <p className="p-6 text-sm text-clinic-muted">No rooms configured.</p>
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: 80 + rooms.length * 130 }}>
              {/* header */}
              <div className="flex border-b border-clinic-primary-lighter bg-clinic-cream/30 sticky top-0">
                <div className="w-16 flex-shrink-0" />
                {rooms.map(r => (
                  <div key={r.id} className="flex-1 min-w-[130px] px-2 py-2 text-sm font-medium text-clinic-dark border-l border-clinic-primary-lighter">
                    {r.name}{r.room_type === 'pod' && <span className="ml-1 text-xs text-clinic-muted">pod</span>}
                  </div>
                ))}
              </div>
              {/* body */}
              <div className="flex">
                <div className="w-16 flex-shrink-0">
                  {hours.map(h => (
                    <div key={h} style={{ height: PX_PER_HOUR }} className="text-[11px] text-clinic-muted text-right pr-2 -mt-1.5 first:mt-0">{hourLabel(h)}</div>
                  ))}
                </div>
                {rooms.map(r => (
                  <div key={r.id} className="relative flex-1 min-w-[130px] border-l border-clinic-primary-lighter" style={{ height: hours.length * PX_PER_HOUR }}>
                    {hours.map((h, i) => <div key={h} className="absolute left-0 right-0 border-t border-clinic-primary-lighter/60" style={{ top: i * PX_PER_HOUR }} />)}
                    {dayBookings.filter(b => b.room_id === r.id).map(b => {
                      const top = ((toMin(b.start) - startHour * 60) / 60) * PX_PER_HOUR;
                      const height = Math.max(18, ((toMin(b.end) - toMin(b.start)) / 60) * PX_PER_HOUR - 2);
                      const ghost = b.bucket === 'pending' || b.bucket === 'released';
                      return (
                        <button
                          key={b.id} type="button" onClick={() => setDetail(b)} data-testid={`cal-booking-${b.id}`}
                          className={`absolute left-1 right-1 rounded-md px-1.5 py-0.5 text-left text-[11px] leading-tight overflow-hidden focus:outline-none focus:ring-2 focus:ring-clinic-primary ${
                            ghost ? 'border border-dashed border-clinic-muted text-clinic-muted bg-white' : 'text-white'}`}
                          style={{ top, height, background: ghost ? undefined : BUCKET_COLOR[b.bucket] }}
                          title={`${b.clinician} · ${timeLabel(b.start)} – ${timeLabel(b.end)} · ${BUCKET_LABEL[b.bucket]}`}
                        >
                          <span className="block font-semibold truncate">{b.clinician}</span>
                          {height > 30 && <span className="block truncate opacity-90">{timeLabel(b.start)} – {timeLabel(b.end)}</span>}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Modal isOpen={detail !== null} onClose={() => setDetail(null)} size="sm"
        title={detail ? detail.clinician : ''}
        description={detail ? `${new Date(`${detail.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })} · ${timeLabel(detail.start)} – ${timeLabel(detail.end)}` : ''}>
        {detail && (
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-clinic-muted">Status</dt>
              <dd className="text-clinic-dark font-medium">{BUCKET_LABEL[detail.bucket]}</dd></div>
            <p className="text-xs text-clinic-muted -mt-1">{BUCKET_HELP[detail.bucket]}</p>
            <div className="flex justify-between"><dt className="text-clinic-muted">Type</dt>
              <dd className="text-clinic-dark">{detail.kind === 'client' ? 'Client session' : detail.kind === 'pod' ? 'Pod' : 'Clinician own use'}</dd></div>
            {detail.client && <div className="flex justify-between"><dt className="text-clinic-muted">Client</dt><dd className="text-clinic-dark">{detail.client}</dd></div>}
            <div className="flex justify-between"><dt className="text-clinic-muted">Room</dt>
              <dd className="text-clinic-dark">{data?.rooms.find(r => r.id === detail.room_id)?.name || 'Unassigned'}</dd></div>
            <div className="flex justify-between"><dt className="text-clinic-muted">Length</dt>
              <dd className="text-clinic-dark">{detail.duration_minutes} min{detail.buffer_minutes ? ` + ${detail.buffer_minutes} min buffer` : ''}</dd></div>
            <p className="text-xs text-clinic-muted pt-2 border-t border-clinic-primary-lighter">
              Booking #{detail.id}. To reschedule, change the duration or cancel, find it in All Bookings.
            </p>
          </dl>
        )}
      </Modal>
    </div>
  );
};

export default ClinicCalendarTab;
